import axios from 'axios';
import React, { useState, useEffect, useRef } from 'react';
import { useHistory, Switch, Route } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ConfirmDialog } from 'primereact/confirmdialog';
import dayjs from 'dayjs';

import {
  REACT_APP_LOGIN_AUTH,
  REACT_APP_AUTH_CLIENT_SECRET,
  REACT_APP_AUTH_CLIENT_ID,
  REACT_APP_AUTH_REFRESH_GRANT_TYPE,
  REACT_APP_YOUTUBE_URL,
  REACT_APP_DW_BETA,
  REACT_APP_CUBE_BASEURL,
  REACT_APP_AUTH_AUTHORIZE_GRANT_TYPE,
} from '../src/constants/envConstants';
import { setSideDrawer } from '../src/redux/slices/page-header.slice';
import '@material-icons/font/css/all.css';
import 'primeflex/primeflex.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import './components/styles/style.css';
import './components/styles/ResponsiveStyle.css';
import ErrorBoundaryComponent from '../src/components/shared/Error/ErrorBoundaryComponent';

import RingCentralIntegration from './components/shared/Topbar/components/ringCentral/RingCentralIntegration';
import { getCookieValue } from './components/Admin/Auth/Login/components/helper/helper';
// import ApiService from './services/api.service';
import { Routes } from './Routes';
import { generateToken, refetchAccessToken } from './services/auth.service';
import useToken from './hooks/useToken';
import { authURL } from './config/variables';
import { getClientDetails } from './components/Admin/Configuration/RestrictAccessbyRole.service';
import { setAccessTokenStorage, setMomentTimezone } from './utils/Helpers';
import globalContext from './contexts/globalContext';
import { AuthRoutes } from './components/Admin/Auth/Routes/AuthRoutes';
import { ReturnToLogin } from './components/Admin/Auth/Routes/ReturnToLogin';
import LoginSkeleton from './components/shared/Skeleton/LoginSkeleton';
import fetchMasterDataHook from './redux/slices/master/fetchMasterDataHook';
import ChangePassword from './components/Admin/Auth/Login/ChangePassword';
import {
  clientDetails,
  StatusColors,
  EVENT_AND_PATH_CONSTANT,
  LOGOUT_TITLES,
  updateClientDetails,
  SOCKET_EVENTS,
} from './constants';
import SessionExpiredPopup from './components/LoginPage/components/SessionExpirePopUp';
import UserBlockedPopup from './components/shared/AccessDenied/UserBlockedPopup';
import { loadChatScript } from './components/shared/chatbot/chatbot';
import PushNotificationProvider from './components/PushNotification/PushNotificationProvider';
import { useConfirmDialogContext } from './contexts/ConfirmDialog';
import { useDailyVersionCheck } from './hooks/useDailyVersionCheck';
import { userAcitivityWorkerHelper } from './userActivityWorker/helper';
import UserSettingsDialog from './components/Admin/Settings/UserSettingsDialog';
import MpinSetupPopup from './components/Admin/Auth/Login/components/MpinSetupPopup';
import AppHeader from './components/shared/Topbar/components/AppHeader';
import {
  enqueueRequest,
  flushQueue,
  getIsValidatingToken,
  setIsValidatingToken,
} from './utils/axiosQueueManager';
import {
  copyLogs,
  downloadLogs,
  storeError406Log,
} from './utils/errorLogsindexedDB';
import websocketService from './components/ProjectManagement/ProjectInfo/services/websocket.service';
import { URL_CONSTANTS } from './constants/urlConstants';

const PASSWORD_EXPIRY_STATUS = {
  EXPIRED: 1,
  EXPIRING: 2,
};
const REFRESH_THRESHOLD = 2 * 60 * 1000;
const LAST_REFRESH_AT_KEY = 'access_token_last_refresh_at';
const MAX_FETCH_TOKEN_RETRIES = 5;
const MAX_ACCESS_TOKEN_RETRIES = 3;

const App = () => {
  const { showConfirmationDialog } = useConfirmDialogContext();
  const history = useHistory();
  const { accessToken, setToken, expireToken } = useToken();
  const [loading, setLoading] = useState(true);
  const [passwordExpiryStatus, setPasswordExpiryStatus] = useState(0);
  const [slideDrawer, setSlideDrawer] = useState(false);
  const [isLoggedOut, setIsLoggedOut] = useState(false);
  const [loggedOutRes, setIsLoggedOutRes] = useState(null);
  const [isUserBlocked, setIsUserBlocked] = useState(false);
  const [userBlockedData, setUserBlockedData] = useState(null);
  const queryParams = new URLSearchParams(window.location.search);
  const code = queryParams.get('code');
  const state = queryParams.get('state');
  const dispatch = useDispatch();
  const permissionsListReducer = useSelector(state => state.permissionsList);
  const permissionList = permissionsListReducer?.permissionList;
  const [visible, setVisible] = useState(false);

  const [myPermission, setMyPermission] = useState(false);
  const loggedInViaPassword = localStorage?.getItem('loggedInViaPassword');
  const loggedInUser = useSelector(state => state?.userProfile?.username);
  useDailyVersionCheck();
  const [showUserSettings, setshowUserSettings] = useState(false);

  const handleUserSettingsVisibility = value => {
    setshowUserSettings(value);
  };
  const isMpinSet = localStorage.getItem('is_mpin_enabled');

  //CHANGED: 3 refs to hold interceptor IDs so we can eject before re-registering
  const requestInterceptorRef = useRef(null);
  const secondRequestInterceptorRef = useRef(null);
  const responseInterceptorRef = useRef(null);

  useEffect(() => {
    if (loggedInViaPassword) {
      const refreshTokenValidity =
        parseInt(clientDetails?.login_configuration?.refresh_token_time) || 0;
      const refreshTokenValidityMs = refreshTokenValidity * 24 * 60 * 60 * 1000;
      const refreshExpiryDate = new Date(Date.now() + refreshTokenValidityMs);
      localStorage.setItem(
        'refresh_token_expiry',
        refreshExpiryDate.toISOString()
      );
    }
  }, []);

  useEffect(() => {
    dispatch(setSideDrawer(slideDrawer));
  }, [slideDrawer]);

  const loginEvent = localStorage?.getItem('syncLoginEvent');
  const userId = localStorage.getItem('user_id');
  const wantSetupMpin = localStorage.getItem('wantSetupMpin');
  const accessTokenExp = localStorage.getItem('accessTokenExpiryTime');

  useEffect(() => {
    if (loggedInUser && loginEvent) {
      userAcitivityWorkerHelper({
        quickSync: true,
        customPayload: {
          action: EVENT_AND_PATH_CONSTANT?.USER_LOGIN?.EVENT,
          data: {
            user_id: userId,
            user_name: loggedInUser,
            date_time: new Date().toISOString(),
          },
          path: EVENT_AND_PATH_CONSTANT?.USER_LOGIN?.PATH,
          project_id: null,
          api_status: true,
        },
      });
      localStorage.removeItem('syncLoginEvent');
    }
  }, [loggedInUser, loginEvent]);

  const fetchMasterData = fetchMasterDataHook();

  useEffect(() => {
    if (accessToken === null || accessToken === '') {
      if (code) {
        generateToken(code, setToken);
        if (state && state !== 'undefined') {
          history.push(decodeURIComponent(state));
        } else {
          window.location = `${document.location.origin}/`;
        }
      }
    }
  }, []);

  const fetchToken = async (retryCount = 0) => {
    try {
      const generatedTokenResponse = await axios.post(
        REACT_APP_LOGIN_AUTH + '/regenerate-token.v1',
        {
          client_id: localStorage.getItem('client_id'),
          user_id: localStorage.getItem('user_id'),
          login_via_password: true,
          client_secret: REACT_APP_AUTH_CLIENT_SECRET,
          device_type: 'web',
          grant_type: REACT_APP_AUTH_AUTHORIZE_GRANT_TYPE,
          secret_client_id: REACT_APP_AUTH_CLIENT_ID,
        }
      );

      if (
        generatedTokenResponse?.data &&
        generatedTokenResponse?.data?.access_token
      ) {
        localStorage.setItem(
          'cubejsAuthorizationToken',
          'Bearer ' + generatedTokenResponse.data.access_token
        );
        if (generatedTokenResponse?.data?.generateResCode) {
          localStorage.setItem(
            'access_token',
            generatedTokenResponse?.data?.generateResCode?.access_token
          );
          localStorage.setItem(
            'accessTokenExpiryTime',
            generatedTokenResponse?.data?.generateResCode?.accessTokenValidUntil
          );

          const accessTokenValidityDays = parseInt(
            generatedTokenResponse?.data?.generateResCode?.access_token_time
          );

          const accessTokenExpiryTime = new Date(
            Date.now() + accessTokenValidityDays * 60 * 1000
          );
          localStorage.setItem(
            'accessTokenExpiryTime',
            generatedTokenResponse?.data?.generateResCode?.accessTokenValidUntil
          );
          document.cookie = `accessToken=${generatedTokenResponse?.data?.generateResCode?.access_token}; expires=${accessTokenExpiryTime.toUTCString()}; path=/`;
          if (generatedTokenResponse?.data?.generateResCode?.refresh_token) {
            localStorage.setItem(
              'refresh_token',
              generatedTokenResponse?.data?.generateResCode?.refresh_token
            );
            const refreshTokenValidityDays = parseInt(
              generatedTokenResponse?.data?.generateResCode?.refresh_token_time
            );
            const refreshTokenExpiryDate = new Date(
              Date.now() + refreshTokenValidityDays * 24 * 60 * 60 * 1000
            );
            document.cookie = `refreshToken=${generatedTokenResponse?.data?.generateResCode?.refresh_token}; expires=${refreshTokenExpiryDate.toUTCString()}; path=/`;
          }

          localStorage.setItem(
            'is_mpin_enabled',
            generatedTokenResponse?.data?.generateResCode?.is_mpin_enabled
          );
        }
      } else {
        if (retryCount > MAX_FETCH_TOKEN_RETRIES) {
          expireToken();
        } else {
          setTimeout(() => {
            fetchToken(retryCount + 1);
          }, 2000);
        }
      }
      localStorage.setItem('loggedInViaPassword', true);
    } catch (error) {
      if (retryCount < MAX_FETCH_TOKEN_RETRIES) {
        setTimeout(() => {
          fetchToken(retryCount + 1);
        }, 2000);
      } else {
        console.error(error);
        expireToken();
      }
    }
  };

  const fetchPasswordExpiryDetails = async () => {
    const passwordExpiryDetails = await axios.get(
      REACT_APP_LOGIN_AUTH + '/password-expiry'
    );
    if (passwordExpiryDetails.data.daysPending) {
      if (passwordExpiryDetails.data.daysPending < 0) {
        setPasswordExpiryStatus(PASSWORD_EXPIRY_STATUS.EXPIRED);
      } else if (passwordExpiryDetails.data.daysPending <= 10) {
        setPasswordExpiryStatus(PASSWORD_EXPIRY_STATUS.EXPIRING);
        showConfirmationDialog({
          message: (
            <div className="flex align-items-center w-full gap-2 surface-border">
              <i
                className="pi pi-exclamation-circle"
                style={{ color: StatusColors.PF_RED, fontSize: 24 }}
              ></i>
              <div className="flex flex-column">
                <span>
                  Your password will expire in{' '}
                  {passwordExpiryDetails.data.daysPending} days.
                </span>
                <span>Please change your password.</span>
              </div>
            </div>
          ),
          header: 'Change Password Confirmation',
          position: 'top',
          acceptLabel: 'Change Password',
          rejectLabel: 'Remind Me Later',
          acceptIcon: 'pi pi-arrow-right',
          accept: () => {
            history.push('/change-password');
          },
          reject: () => {
            localStorage.setItem('isShowPasswordChangeConfirmation', false);
          },
        });
      }
    }
  };

  const fetchAccessToken = async (retryCount = 0) => {
    const refreshToken = getCookieValue('refreshToken');
    try {
      const res = await axios.post(
        REACT_APP_LOGIN_AUTH + '/auth/regenerate-access-token',
        {
          refresh_token: refreshToken
            ? refreshToken
            : localStorage?.getItem('refresh_token'),
          client_secret: REACT_APP_AUTH_CLIENT_SECRET,
          device_type: 'web',
          grant_type: REACT_APP_AUTH_REFRESH_GRANT_TYPE,
          secret_client_id: REACT_APP_AUTH_CLIENT_ID,
          extractionData: {
            originPage: window.location.pathname,
            originHref: window.location.href,
            time: Date.now(),
            userId: localStorage.getItem('user_id'),
            device: navigator.userAgent,
            localStorage: {
              LAST_REFRESH_AT_KEY: localStorage.getItem(LAST_REFRESH_AT_KEY),
              loggedInViaPassword: localStorage.getItem('loggedInViaPassword'),
              is_mpin_enabled: localStorage.getItem('is_mpin_enabled'),
              refresh_token_expiry: localStorage.getItem(
                'refresh_token_expiry'
              ),
              loginTime: localStorage.getItem('loginTime'),
            },
          },
        }
      );

      if (res?.data?.status === true) {
        if (res?.data?.generateResCode?.cube_access_token) {
          localStorage.setItem(
            'cubejsAuthorizationToken',
            'Bearer ' + res?.data?.generateResCode?.cube_access_token
          );
        }
        if (res?.data?.generateResCode?.access_token) {
          const accessTokenExpiryDate = new Date(
            Date.now() +
              res?.data?.generateResCode?.access_token_time * 60 * 1000
          ).toUTCString();
          setAccessTokenStorage(
            res?.data?.generateResCode?.access_token,
            accessTokenExpiryDate
          );
        }
        localStorage.removeItem('force_access_token_refresh');
      } else {
        if (retryCount < MAX_ACCESS_TOKEN_RETRIES && res?.status !== 406) {
          setTimeout(() => {
            fetchAccessToken(retryCount + 1);
          }, 2000);
        } else {
          fetchToken();
        }
      }
    } catch (error) {
      console.error('Error refreshing access token:', error);

      // If system is offline, wait for internet
      if (!navigator.onLine) {
        console.log('System is offline. Waiting for internet...');

        const handleOnline = () => {
          console.log('Back online');
          window.removeEventListener('online', handleOnline);
        };

        window.addEventListener('online', handleOnline);
        return;
      }

      // If online but failed
      if (retryCount < MAX_ACCESS_TOKEN_RETRIES) {
        setTimeout(() => {
          fetchAccessToken(retryCount + 1);
        }, 2000);
      } else {
        fetchToken(); // final fallback
      }
    }
  };

  useEffect(() => {
    if (!loggedInViaPassword) {
      if (localStorage.getItem('client_id')) {
        fetchToken();
      }
    }
  }, [localStorage.getItem('client_id')]);

  useEffect(() => {
    if (
      localStorage.getItem('client_id') &&
      !localStorage.getItem('isShowPasswordChangeConfirmation') &&
      accessToken
    ) {
      fetchPasswordExpiryDetails();
    }
  }, [localStorage.getItem('client_id'), accessToken]);

  // Now wrapped in useEffect with eject+cleanup so only ONE interceptor is ever active
  useEffect(() => {
    const refreshToken =
      getCookieValue('refreshToken') || localStorage.getItem('refresh_token');

    if (requestInterceptorRef.current !== null) {
      axios.interceptors.request.eject(requestInterceptorRef.current);
    }

    requestInterceptorRef.current = axios.interceptors.request.use(
      async config => {
        try {
          if (config.skipAuthInterceptor) {
            return config;
          }

          const accessToken = localStorage.getItem('access_token');
          const loginTime = localStorage.getItem('loginTime');
          const freshAccessTokenExp = localStorage.getItem(
            'accessTokenExpiryTime'
          );
          const isExpired = !dayjs().isSame(dayjs(loginTime), 'day');
          if (accessToken && refreshToken) {
            if (isExpired) {
              if (isMpinSet === 'true') {
                localStorage.setItem('sessionPaused', 'true');
                localStorage.removeItem('access_token');
                document.location = `${document.location.origin}/verify-pin`;
                return null;
              } else {
                expireToken();
              }
            }

            if (!accessTokenExp) {
              try {
                const data = await refetchAccessToken(accessToken); // retries on 500, throws otherwise

                const newToken = data?.data?.accessToken;

                setAccessTokenStorage(
                  newToken,
                  data?.data?.accessTokenValidUntil
                );
                localStorage.setItem(
                  'checkAccessToken.v1_API_success_time',
                  dayjs().utc().format()
                );
                axios.defaults.headers.common['Authorization'] =
                  `Bearer ${newToken}`;
                config.headers['Authorization'] = `Bearer ${newToken}`;

                flushQueue(null, newToken);
                return config;
              } catch (err) {
                console.error('refresh error', err);
                flushQueue(err, null);
              }
            }
            // ACCESS TOKEN EXPIRY CHECK
            if (
              config?.data?.grant_type !== REACT_APP_AUTH_REFRESH_GRANT_TYPE
            ) {
              delete axios.defaults.headers.common['Authorization'];
              if (
                !config?.url.includes(`${authURL}/token`) &&
                !config?.url.includes(REACT_APP_YOUTUBE_URL) &&
                !config?.url.includes(REACT_APP_CUBE_BASEURL)
              ) {
                const expiryTime = freshAccessTokenExp
                  ? new Date(freshAccessTokenExp).getTime()
                  : NaN;
                const shouldRefresh =
                  isNaN(expiryTime) ||
                  expiryTime - Date.now() <= REFRESH_THRESHOLD;

                if (!shouldRefresh) {
                  flushQueue(null, accessToken);
                  config.headers['Authorization'] = `Bearer ${accessToken}`;
                  setIsValidatingToken(false);
                  return config;
                } else {
                  if (getIsValidatingToken()) {
                    return await enqueueRequest(config);
                  }

                  setIsValidatingToken(true);
                  // REFRESH / QUEUE LOGIC
                  try {
                    const data = await refetchAccessToken(accessToken); // retries on 500, throws otherwise
                    const newToken = data?.data?.accessToken;
                    setAccessTokenStorage(
                      newToken,
                      data?.data?.accessTokenValidUntil
                    );
                    localStorage.setItem(
                      'checkAccessToken.v1_API_success_time',
                      dayjs().utc().format()
                    );
                    axios.defaults.headers.common['Authorization'] =
                      `Bearer ${newToken}`;
                    config.headers['Authorization'] = `Bearer ${newToken}`;

                    flushQueue(null, newToken);
                    return config;
                    // throw new Error('FORCED_REFRESH_DISABLED');
                  } catch (err) {
                    console.error('refresh error', err);
                    flushQueue(err, null);
                  } finally {
                    setIsValidatingToken(false);
                  }
                }

                setTimeout(() => {
                  if (!sessionStorage.getItem('reloadedOnce')) {
                    sessionStorage.setItem('reloadedOnce', 'true');
                    axios.defaults.headers.common['client-id'] =
                      localStorage.getItem('client_id');
                  }
                }, 500);
              }
            }
          } else if (
            localStorage.getItem('Auth_Verify') &&
            !window.location.pathname.includes('/verify-pin') &&
            !config?.url?.toString()?.includes('/auth/login-mpin')
          ) {
            document.location = `${document.location.origin}/verify-pin`;
            return null;
          }
          return config;
        } catch (err) {
          console.error(err);
          expireToken();
        }
      }
    );
    return () => {
      axios.interceptors.request.eject(requestInterceptorRef.current);
    };
  }, []);

  // CHANGED: added eject before re-registering — previously stacked a new interceptor on every client_id change
  useEffect(() => {
    if (secondRequestInterceptorRef.current !== null) {
      axios.interceptors.request.eject(secondRequestInterceptorRef.current);
    }
    secondRequestInterceptorRef.current = axios.interceptors.request.use(
      async config => {
        try {
          if (
            ![
              `${authURL}/token`,
              `${authURL}/get-all-user-tenants`,
              REACT_APP_YOUTUBE_URL,
            ].includes(config?.url)
          ) {
            axios.defaults.headers.common['client-id'] =
              localStorage.getItem('client_id');
            axios.defaults.headers.common['device-type'] = 'web_app';
          }
          return config;
        } catch (err) {
          console.error('interceptor', err);
        }
      }
    );
    return () => {
      axios.interceptors.request.eject(secondRequestInterceptorRef.current);
    };
  }, [localStorage.getItem('client_id')]);

  // Now wrapped in useEffect with eject+cleanup so only ONE response interceptor is ever active
  useEffect(() => {
    if (responseInterceptorRef.current !== null) {
      axios.interceptors.response.eject(responseInterceptorRef.current);
    }
    responseInterceptorRef.current = axios.interceptors.response.use(
      response => {
        return response;
      },
      error => {
        if (!localStorage.getItem('webPermission')) {
          localStorage.setItem('webPermission', 'false');
        }
        const isBlockedUserResponse =
          error?.response?.data?.errorCode === 403 &&
          error?.response?.data?.message === 'User is blocked';
        if (isBlockedUserResponse) {
          setUserBlockedData(error?.response?.data);
          setIsUserBlocked(true);
          return Promise.reject(error);
        }
        if (
          error?.status === 450 &&
          (localStorage.getItem('webPermission') === 'false' ||
            localStorage.getItem('webPermission') === 'true') &&
          accessToken
        ) {
          localStorage.setItem('webPermission', 'true');
          history.push('/web-access-denied');
          setMyPermission(true);
        }
        const refreshTokenExpDate = new Date(
          localStorage.getItem('refresh_token_expiry')
        )
          .toISOString()
          .split('T')[0];
        const currentDate = new Date().toISOString().split('T')[0];
        const loginTime = localStorage.getItem('loginTime');
        const isExpired = !dayjs().isSame(dayjs(loginTime), 'day');
        if (
          error?.response?.status == 406 &&
          !isExpired &&
          !LOGOUT_TITLES?.includes(
            error?.response?.data?.message ||
              error?.response?.data?.title ||
              error?.response?.data?.message?.title
          )
        ) {
          const isSessionPaused = localStorage.getItem('sessionPaused');
          const connection = navigator?.connection;

          if (isSessionPaused !== 'true') {
            const localStorageSnapshot = { ...localStorage };

            storeError406Log({
              error: {
                name: error?.name || 'API_ERROR',
                message: error?.message || error?.response?.data?.message,
                stack: error?.stack || null,
              },
              user_id:
                localStorageSnapshot?.user_id ||
                localStorage.getItem('user_id'),
              client_id:
                localStorageSnapshot?.client_id ||
                localStorage.getItem('client_id'),
              time: dayjs().utc().format(),
              url: `${window.location.href}`,
              deviceInfo: {
                device: 'web',
                userAgent: navigator?.userAgentData?.brands,
                platform: navigator.platform,
                cores: navigator.hardwareConcurrency,
                memory: navigator.deviceMemory,
                screen: `${window.screen.width}x${window.screen.height}`,
              },
              connectionInfo: {
                bandwidth: connection.effectiveType,
                speed: connection?.downlink,
                roundTripTime: `${connection?.rtt} ms`,
                dataSaverEnabled: connection?.saveData,
              },
              api: {
                apiEndPoint: error?.config?.url,
                method: error?.config?.method,
                status: error?.response?.status,
                response: error?.response?.data,
              },
              requestToken:
                error?.config?.headers?.Authorization ||
                error?.config?.headers?.authorization,
              localStorageKeys: {
                loggedInUser: localStorageSnapshot?.logged_in_user,
                loggedInUserEmail: localStorageSnapshot?.loggedInUserEmail,
                loggedInUserId: localStorageSnapshot?.user_id,
                loginTime: localStorageSnapshot?.loginTime,
                roleId: localStorageSnapshot?.role_id,
                roleName: localStorageSnapshot?.role_name,
                refreshToken: localStorageSnapshot?.refresh_token,
                refreshTokenExp: localStorageSnapshot?.refresh_token_expiry,
                mpinEnabled: localStorageSnapshot?.is_mpin_enabled,
                lastVersionChecked: localStorageSnapshot?.last_version_check,
                accessToken: localStorageSnapshot?.access_token,
                accessTokenExpiryTime:
                  localStorageSnapshot?.accessTokenExpiryTime,
                appVersion: localStorageSnapshot?.app_version,
                clientId: localStorageSnapshot?.client_id,
                loggedInViaPassword: localStorageSnapshot?.loggedInViaPassword,
                isInstaller: localStorageSnapshot?.is_installer,
                installerId: localStorageSnapshot?.installer_id,
                checkAccessTokenApiCallTime:
                  localStorageSnapshot?.[
                    'checkAccessToken.v1_API_success_time'
                  ] ?? null,
              },
            });
          }
        }
        if (error?.status === 406 && !isLoggedOut) {
          if (refreshTokenExpDate <= currentDate) {
            setIsLoggedOutRes(error?.response?.data);
            setIsLoggedOut(true);
          }
          userAcitivityWorkerHelper({
            quickSync: true,
            customPayload: {
              action: EVENT_AND_PATH_CONSTANT?.USER_LOGOUT?.EVENT,
              data: {
                user_id: userId,
                user_name: localStorage.getItem('logged_in_user'),
                date_time: new Date().toISOString(),
              },
              path: EVENT_AND_PATH_CONSTANT?.USER_LOGOUT?.PATH,
              project_id: null,
              api_status: true,
            },
          });
          return;
        }
        if (
          error.response &&
          error.response.data &&
          (error.response.data?.name === 'TokenExpiredError' ||
            error.response.data?.message === 'jwt expired')
        ) {
          const localStorageSnapshot = { ...localStorage };

          storeError406Log({
            error: {
              name: error?.name || 'API_ERROR',
              message: error?.message || error?.response?.data?.message,
              stack: error?.stack || null,
            },
            user_id:
              localStorageSnapshot?.user_id || localStorage.getItem('user_id'),
            client_id:
              localStorageSnapshot?.client_id ||
              localStorage.getItem('client_id'),
            time: dayjs().utc().format(),
            url: `${window.location.href}`,
            deviceInfo: {
              device: 'web',
              userAgent: navigator?.userAgentData?.brands,
              platform: navigator.platform,
              cores: navigator.hardwareConcurrency,
              memory: navigator.deviceMemory,
              screen: `${window.screen.width}x${window.screen.height}`,
            },
            api: {
              apiEndPoint: error?.config?.url,
              method: error?.config?.method,
              status: error?.response?.status,
              response: error?.response?.data,
            },
            requestToken:
              error?.config?.headers?.Authorization ||
              error?.config?.headers?.authorization,
            localStorageKeys: {
              loggedInUser: localStorageSnapshot?.logged_in_user,
              loggedInUserEmail: localStorageSnapshot?.loggedInUserEmail,
              loggedInUserId: localStorageSnapshot?.user_id,
              loginTime: localStorageSnapshot?.loginTime,
              roleId: localStorageSnapshot?.role_id,
              roleName: localStorageSnapshot?.role_name,
              refreshToken: localStorageSnapshot?.refresh_token,
              refreshTokenExp: localStorageSnapshot?.refresh_token_expiry,
              mpinEnabled: localStorageSnapshot?.is_mpin_enabled,
              lastVersionChecked: localStorageSnapshot?.last_version_check,
              accessToken: localStorageSnapshot?.access_token,
              accessTokenExpiryTime:
                localStorageSnapshot?.accessTokenExpiryTime,
              appVersion: localStorageSnapshot?.app_version,
              clientId: localStorageSnapshot?.client_id,
              loggedInViaPassword: localStorageSnapshot?.loggedInViaPassword,
              isInstaller: localStorageSnapshot?.is_installer,
              installerId: localStorageSnapshot?.installer_id,
              checkAccessTokenApiCallTime:
                localStorageSnapshot?.[
                  'checkAccessToken.v1_API_success_time'
                ] ?? null,
            },
          });
          expireToken();
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.response.eject(responseInterceptorRef.current);
    };
  }, []); // empty deps — registers only once on mount

  // download error Log using console.terminal
  useEffect(() => {
    window.copyLogs = copyLogs;
    window.downloadLogs = downloadLogs;
  }, []);

  // download error Log using Url Params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (params.has('downloadLogs')) {
      downloadLogs();

      params.delete('downloadLogs');
      const newUrl =
        window.location.pathname +
        (params.toString() ? `?${params.toString()}` : '');

      window.history.replaceState(null, '', newUrl);
    }
  }, []);

  useEffect(() => {
    const shouldForceRefresh = localStorage.getItem(
      'force_access_token_refresh'
    );
    const refreshToken =
      getCookieValue('refreshToken') || localStorage.getItem('refresh_token');

    if (shouldForceRefresh === 'true' && refreshToken) {
      fetchAccessToken();
    }
  }, []);

  useEffect(() => {
    if (clientDetails?.cx_scheduling_module_beta) {
      websocketService.initialize(URL_CONSTANTS.DOCUMENT.baseUrl);

      const unsubscribe = websocketService.on(
        SOCKET_EVENTS.CLIENT_SCHEDULING_LOCKED_USER,
        data => {
          localStorage?.setItem(
            'cx_scheduling_module_beta_button',
            data?.locked
          );
          window.dispatchEvent(new Event('cxSchedulingUpdated'));
        }
      );

      return () => {
        unsubscribe();
      };
    }
  }, []);

  if (accessToken) {
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  }

  const setClientDetails = async () => {
    setLoading(true);
    const clientId = localStorage.getItem('client_id');
    const response = await getClientDetails();
    if (response) {
      const isTentativeSchedulingEnabled =
        response?.tenant_permissions[0]?.included_permissions?.includes(
          'ENABLE_TENTATIVE_SCHEDULING'
        );
      localStorage.setItem(
        'isTentativeSchedulingEnabled',
        isTentativeSchedulingEnabled
      );
      localStorage?.setItem(
        'isClientLockedForOnlineSchedulingBeta',
        response?.is_client_locked_for_online_scheduling
      );
      localStorage.setItem('client_timezone', response.client_timezone);
      setMomentTimezone(response.client_timezone);
      localStorage.setItem('client_details', JSON.stringify(response));
      updateClientDetails(response);
      if (!localStorage.getItem('project_items_view')) {
        localStorage.setItem(
          'project_items_view',
          response.show_old_project_items === 1 ? 'old_view' : 'new_view'
        );
      }
      if (localStorage.getItem('show_new_resource_list') === null) {
        localStorage.setItem(
          'show_new_resource_list',
          response.scheduler_config?.show_new_resource_list
        );
      }

      if (response?.cx_scheduling_module_beta) {
        websocketService.emitEvent('register_client', {
          clientId,
        });
      }
    }
    setLoading(false);
    fetchMasterData();
  };

  useEffect(() => {
    const remindDateStr = document.cookie
      .split('; ')
      .find(row => row.startsWith('setMpinPopuptime='))
      ?.split('=')[1];
    if (isMpinSet === 'false' && !remindDateStr) {
      setVisible(true);
    }
    if (accessToken && wantSetupMpin === 'yes') {
      setshowUserSettings(true);
      setVisible(false);
    }
  }, [accessToken, wantSetupMpin, isMpinSet]);

  useEffect(() => {
    if (accessToken) {
      if (axios.defaults.headers.common['client-id']) {
        setClientDetails();
        const removeScripts = loadChatScript();
        return () => {
          removeScripts();
        };
      }
    }
  }, [accessToken, permissionList]);

  if (code) {
    if (!loading) {
      const client_id = localStorage.getItem('client_id');
      if (client_id) {
        const allowedClients = REACT_APP_DW_BETA?.split(',') || [];
        const checkNewClient = allowedClients.includes(client_id);
        if (checkNewClient) {
          history.push('/dashboard');
        }
      }
    } else {
      return <LoginSkeleton />;
    }
  }
  if (accessToken) {
    if (history.location.pathname === '/') {
      history.push('/dashboard');
    }
  }
  if (passwordExpiryStatus == PASSWORD_EXPIRY_STATUS.EXPIRED) {
    return (
      <ChangePassword
        message={
          'Your password has expired. Please change your password to continue using your account.'
        }
      />
    );
  }

  if (isUserBlocked) {
    return <UserBlockedPopup open={isUserBlocked} data={userBlockedData} />;
  }

  if (isLoggedOut) {
    return <SessionExpiredPopup open={isLoggedOut} data={loggedOutRes} />;
  }

  return accessToken ? (
    <>
      <PushNotificationProvider setLoading={setLoading} />
      {visible && <MpinSetupPopup visible={visible} setVisible={setVisible} />}
      <globalContext.Provider value={accessToken}>
        <AppHeader
          setSlideDrawer={setSlideDrawer}
          slideDrawer={slideDrawer}
          myPermission={myPermission}
        />

        <div
          className={`grid grid-nogutter pr-3 py-3 transition-ease-in-out transition-duration-500 light-grey-bg-100 main-body ${slideDrawer && 'pl-7rem'}`}
        >
          <ErrorBoundaryComponent>
            <Routes webPermission={myPermission} />
          </ErrorBoundaryComponent>
        </div>
        <ConfirmDialog />
        <RingCentralIntegration />
      </globalContext.Provider>
      {showUserSettings ? (
        <UserSettingsDialog
          showDialog={showUserSettings}
          handleCloseDialogue={handleUserSettingsVisibility}
          defaultTabIndex={2}
        />
      ) : null}
    </>
  ) : (
    <ErrorBoundaryComponent>
      <Switch>
        {AuthRoutes.map((route, index) => (
          <Route
            key={index}
            path={route.path}
            exact={true}
            component={route.component}
          />
        ))}
        <Route path="*" exact={true} component={ReturnToLogin} />
      </Switch>
    </ErrorBoundaryComponent>
  );
};

export default App;
